// Story 6.9 (ADO-1163, AC3) — Form Field (label + input). Figma 572:3533.
//
// a11y (NFR24–26): explicit <label htmlFor> association; helper/error text is
// wired via aria-describedby; the invalid state sets aria-invalid. Read-only
// fields (My Account's Auth0/claim-owned values) render with `readOnly` so they
// are announced as non-editable rather than disabled (still focusable/copyable).

import { useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import styles from './FormField.module.css';

export interface FormFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label: ReactNode;
  /** Optional helper text shown below the field. */
  helperText?: ReactNode;
  /** Error message; when set the field renders its invalid state. */
  error?: ReactNode;
  id?: string;
}

export function FormField({
  label,
  helperText,
  error,
  id,
  className,
  readOnly,
  ...rest
}: FormFieldProps) {
  const reactId = useId();
  const fieldId = id ?? reactId;
  const helperId = helperText ? `${fieldId}-helper` : undefined;
  const errorId = error ? `${fieldId}-error` : undefined;
  const describedBy = [errorId, helperId].filter(Boolean).join(' ') || undefined;

  const inputClasses = [
    styles.input,
    readOnly ? styles.readOnly : '',
    error ? styles.invalid : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.field}>
      <label className={styles.label} htmlFor={fieldId}>
        {label}
      </label>
      <input
        id={fieldId}
        className={inputClasses}
        readOnly={readOnly}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        {...rest}
      />
      {/* Both can render together: when an error is set the helper still shows,
          so every id in aria-describedby resolves to a real node (otherwise axe
          flags aria-valid-attr-value and the helper text is silently dropped). */}
      {error ? (
        <span id={errorId} className={`${styles.helper} ${styles.error}`} role="alert">
          {error}
        </span>
      ) : null}
      {helperText ? (
        <span id={helperId} className={styles.helper}>
          {helperText}
        </span>
      ) : null}
    </div>
  );
}
